import { Backdrop, Box, Fade, Modal } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../../../components/Loader/Loader';
import ModalVerification from '../../../../components/ModalVerification/ModalVerification';
import { formatPhoneNumber } from '../../../../constants/formatPhoneNumber';
import user from '../../../../constants/images/avatar.svg';
import logo from '../../../../constants/images/logo.svg';
import trashIcon from '../../../../constants/images/trash-icon.svg';
import upload from '../../../../constants/images/upload-icon.svg';
import { cleanerService } from '../../../../services/cleaner.service';
import { registrationCleanerActions } from '../../../../store/slices/registrationCleanerSlice';
import { compressImage, compressDocuments } from '../../../../utils/imageCompression';
import styles from './RegistrationPageCleanerStep3.module.scss';

const RegistrationPageCleanerStep3 = ({ setCurrentStep, setIsCleaner }) => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProfilePhoto, setSelectedProfilePhoto] = useState(null);
  const [selectedPassportPhoto, setSelectedPassportPhoto] = useState(null);

  const dispatch = useDispatch();
  const {
    passportPhotos,
    profilePhoto,
    user_data,
    taxpayer_number,
    city,
    has_experience,
    years_of_experience,
    is_punctual,
    is_stress_resistant,
    speed_of_cleaning,
    quality_of_cleaning,
    teamwork,
    has_own_equipment,
    credit_cart_number,
  } = useSelector((state) => state.registrationCleaner);

  const createFormData = useCallback(
    (profilePhoto, passportPhotos) => {
      const formData = new FormData();

      const has_experience_boolean = has_experience.toLowerCase() === 'так';
      const has_own_equipment_boolean = has_own_equipment.toLowerCase() === 'так';
      const teamwork_boolean = teamwork.toLowerCase() === 'так';

      const cleaner_related_data = {
        credit_cart_number,
        taxpayer_number,
        city,
        has_experience: has_experience_boolean,
        years_of_experience,
        is_punctual,
        is_stress_resistant,
        speed_of_cleaning,
        quality_of_cleaning,
        teamwork: teamwork_boolean,
        has_own_equipment: has_own_equipment_boolean,
      };

      const transformedUserData = {
        ...user_data,
        invited_by: user_data?.invited_by ? Number(user_data?.invited_by) : null,
        mobile_nummer: formatPhoneNumber(user_data.mobile_nummer),
      };

      formData.append('user_data', JSON.stringify(transformedUserData));
      formData.append('cleaner_related_data', JSON.stringify(cleaner_related_data));
      if (profilePhoto) {
        formData.append('photos_data', profilePhoto);
      }

      for (const photo of passportPhotos) {
        formData.append('documents_photos_data', photo);
      }

      return formData;
    },
    [user_data, has_experience, has_own_equipment, teamwork, taxpayer_number, city, years_of_experience, is_punctual, is_stress_resistant, speed_of_cleaning, quality_of_cleaning, credit_cart_number]
  );

  const handleShowPhoto = useCallback((type, file) => {
    const url = URL.createObjectURL(file);
    if (type === 'passport') {
      setSelectedPassportPhoto(url);
    } else {
      setSelectedProfilePhoto(url);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedProfilePhoto(null);
    setSelectedPassportPhoto(null);
  }, []);

  const handleAddPassportPhoto = useCallback(() => {
    document.getElementById('fileInputPassport').click();
  }, []);

  const handleGetPassportPhotosFromInput = useCallback(
    async (e) => {
      const files = Array.from(e.target.files);

      if (files.length > 0) {
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];

        for (const file of files) {
          if (!allowedTypes.includes(file.type)) {
            setError('Дозволені тільки файли типу PNG, JPG, JPEG');
            return;
          }

          const maxSize = 20 * 1024 * 1024;
          if (file.size > maxSize) {
            setError('Максимальний розмір файлу - 20 МБ');
            return;
          }
        }

        if (passportPhotos.length + files.length > 5) {
          setError('Ви не можете вибрати більше 5 фото');
        } else {
          // Compress passport scans gently (near-lossless) before upload.
          const compressed = await compressDocuments(files);
          dispatch(registrationCleanerActions.setPassportPhotos([...passportPhotos, ...compressed]));
        }
      }
    },
    [dispatch, passportPhotos]
  );

  const handleDeletePassportPhoto = useCallback(
    (event, index) => {
      event.stopPropagation();
      const updatedFiles = [...passportPhotos];
      updatedFiles.splice(index, 1);
      dispatch(registrationCleanerActions.setPassportPhotos(updatedFiles));
    },
    [dispatch, passportPhotos]
  );

  const handleAddProfilePhoto = useCallback(() => {
    document.getElementById('fileInputProfile').click();
  }, []);

  const handleGetProfilePhotoFromInput = useCallback(
    async (e) => {
      const file = e.target.files[0];

      if (file) {
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
          setError('Дозволені тільки файли типу PNG, JPG, JPEG');
          return;
        }

        const maxSize = 20 * 1024 * 1024; // 20 МB in bites
        if (file.size > maxSize) {
          setError('Максимальний розмір файлу - 20 МБ');
          return;
        }

        // Shrink the profile photo in the browser before upload (falls back to
        // the original). Passports use the gentler document preset below.
        dispatch(registrationCleanerActions.setProfilePhoto(await compressImage(file)));
      }
    },
    [dispatch]
  );

  const handleCreateProfile = useCallback(async () => {
    setIsLoading(true);

    const formData = createFormData(profilePhoto, passportPhotos);

    try {
      await cleanerService.create(formData);
      dispatch(registrationCleanerActions.clearAllData());
      setIsModalOpen(true);
    } catch (error) {
      setError(error?.response?.data);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [createFormData, profilePhoto, passportPhotos, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(JSON.stringify(error), { duration: 5000, id: 'error' });
      setError(null);
    }
  }, [error]);

  useEffect(() => {
    return () => {
      toast.remove('error');
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      dispatch(registrationCleanerActions.clearAllData());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dispatch]);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 4,
  };

  return isLoading ? (
    <Loader />
  ) : (
    <div className={styles.registrationPageCleanerStep3}>
      <div className={styles.registrationPageCleanerStep3_registration}>
        <div className={styles.registrationPageCleanerStep3_registration_logo}>
          <img src={logo} alt="logo icon" />
        </div>

        <h2 className={styles.registrationPageCleanerStep3_registration_title}>
          <span>3/3</span> Реєстрація
        </h2>

        <div className={styles.registrationPageCleanerStep3_registration_passport}>
          <p>Фотографії паспорта / ID-картки</p>

          <input
            type="file"
            id="fileInputPassport"
            style={{ display: 'none' }}
            multiple
            onChange={handleGetPassportPhotosFromInput}
            autoComplete="off"
          />
          <div onClick={handleAddPassportPhoto} className={styles.addButton}>
            <div>
              <img src={upload} alt="upload photo icon" />
              <p>Формат: PNG, JPG, JPEG (Макс. 20мб)</p>
            </div>

            <p>Натисніть для завантаження фото паспорта / ID-картки</p>
          </div>

          <div className={styles.registrationPageCleanerStep3_registration_passport_photos}>
            {passportPhotos.map((file, index) => (
              <div
                onClick={() => handleShowPhoto('passport', file)}
                key={index}
                className={styles.photo_item}
                style={{ backgroundImage: `url(${URL.createObjectURL(file)}` }}
              >
                <div onClick={(event) => handleDeletePassportPhoto(event, index)}>
                  <img src={trashIcon} alt="trash-icon" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedPassportPhoto && (
          <div className={styles.modal} onClick={handleCloseModal}>
            <span className={styles.close} onClick={handleCloseModal}>
              ×
            </span>
            <div
              style={{ backgroundImage: `url(${selectedPassportPhoto})` }}
              className={styles.modalContent_passport}
            ></div>
          </div>
        )}

        <div className={styles.registrationPageCleanerStep3_registration_profilePhoto}>
          <p>Профільна фотографія</p>

          <div className={styles.registrationPageCleanerStep3_registration_profilePhoto_photo}>
            <div
              onClick={() => !!profilePhoto && handleShowPhoto('profile', profilePhoto)}
              style={{
                backgroundImage: `url(${!!profilePhoto ? URL.createObjectURL(profilePhoto) : user})`,
              }}
              className={styles.registrationPageCleanerStep3_registration_profilePhoto_photo_avatar}
            ></div>

            {selectedProfilePhoto && (
              <div className={styles.modal} onClick={handleCloseModal}>
                <span className={styles.close} onClick={handleCloseModal}>
                  ×
                </span>
                <img className={styles.modalContent_profile} src={selectedProfilePhoto} />
              </div>
            )}

            <input
              type="file"
              id="fileInputProfile"
              style={{ display: 'none' }}
              onChange={handleGetProfilePhotoFromInput}
              autoComplete="off"
            />
            <div onClick={handleAddProfilePhoto} className={styles.addButton}>
              <div>
                <img src={upload} alt="upload photo icon" />
                <p>Формат: PNG, JPG, JPEG (Макс. 20мб)</p>
              </div>

              <p>Натисніть для завантаження вашої фотографії</p>
            </div>
          </div>
        </div>

        <div className={styles.registrationPageCleanerStep3_registration_buttons}>
          <button onClick={() => setCurrentStep((prev) => prev - 1)} className={styles.prev}>
            Назад
          </button>
          <button
            disabled={!profilePhoto || Object.keys(passportPhotos)?.length === 0}
            onClick={handleCreateProfile}
            className={styles.create}
          >
            Створити Профіль
          </button>
        </div>
      </div>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={isModalOpen}
        onClose={setIsModalOpen}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={isModalOpen}>
          <Box sx={style}>
            <ModalVerification />
          </Box>
        </Fade>
      </Modal>

      <Toaster />
      <div className={styles.registrationPageCleanerStep3_icon}></div>
    </div>
  );
};

export default RegistrationPageCleanerStep3;
